import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { formatMoney } from "../money";
import type {
  BillingCadence,
  GeneratedQuotationFile,
  Quotation,
  QuotationExportSnapshot,
} from "../model";
import {
  createQuotationFileName,
  lineAmountLabel,
  lineItemsForCadence,
  lineQuantityLabel,
  lineUnitPriceLabel,
} from "./shared";
import { createQuotationExportSnapshot } from "./snapshot";

interface PdfWithTableState extends jsPDF {
  lastAutoTable?: {
    finalY: number;
  };
}

const PAGE_MARGIN = 48;
const CONTENT_WIDTH = 516;
const PAGE_BOTTOM = 744;

function ensureSpace(pdf: jsPDF, y: number, needed: number): number {
  if (y + needed <= PAGE_BOTTOM) {
    return y;
  }

  pdf.addPage();
  return PAGE_MARGIN;
}

function addTextBlock(
  pdf: jsPDF,
  text: string,
  x: number,
  y: number,
  width: number,
  lineHeight = 14,
): number {
  if (text.trim().length === 0) {
    return y;
  }

  const lines = pdf.splitTextToSize(text, width) as string[];
  pdf.text(lines, x, y);
  return y + lines.length * lineHeight;
}

function addItemsTable(
  pdf: PdfWithTableState,
  snapshot: QuotationExportSnapshot,
  cadence: BillingCadence,
  startY: number,
): number {
  const items = lineItemsForCadence(snapshot, cadence);
  if (items.length === 0) {
    return startY;
  }

  const heading = cadence === "recurring" ? "Recurring charges" : "One-time charges";
  const headingY = ensureSpace(pdf, startY, 64);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(23, 25, 29);
  pdf.text(heading, PAGE_MARGIN, headingY);

  autoTable(pdf, {
    startY: headingY + 9,
    margin: {
      left: PAGE_MARGIN,
      right: PAGE_MARGIN,
      top: PAGE_MARGIN,
      bottom: PAGE_MARGIN,
    },
    head: [["Item", "Qty / unit", "Unit price", "Amount"]],
    body: items.map((lineItem) => [
      [lineItem.description, lineItem.details, lineItem.provenance.label]
        .filter((part) => part.trim().length > 0)
        .join("\n"),
      lineQuantityLabel(lineItem),
      lineUnitPriceLabel(snapshot, lineItem),
      lineAmountLabel(snapshot, lineItem),
    ]),
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 8.5,
      cellPadding: 5,
      lineColor: [215, 217, 222],
      lineWidth: 0.5,
      textColor: [23, 25, 29],
      overflow: "linebreak",
      valign: "top",
    },
    headStyles: {
      fillColor: [244, 241, 234],
      textColor: [23, 25, 29],
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 246 },
      1: { cellWidth: 80 },
      2: { cellWidth: 90, halign: "right" },
      3: { cellWidth: 100, halign: "right" },
    },
  });

  return (pdf.lastAutoTable?.finalY ?? headingY + 50) + 18;
}

function createPdfDocument(snapshot: QuotationExportSnapshot): jsPDF {
  const quotation = snapshot.quotation;
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "letter",
    compress: false,
    putOnlyUsedFonts: true,
  }) as PdfWithTableState;

  pdf.setProperties({
    title: "Quotation",
    subject: "Locally generated quotation",
    author: "Quotation and QR Sender",
    creator: "Quotation and QR Sender",
    keywords: "quotation",
  });

  let y = PAGE_MARGIN;
  pdf.setTextColor(23, 25, 29);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(17);
  pdf.text(quotation.seller.name, PAGE_MARGIN, y);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.5);
  pdf.setTextColor(93, 98, 107);
  y = addTextBlock(
    pdf,
    [quotation.seller.address, quotation.seller.email, quotation.seller.phone]
      .filter((value) => value.trim().length > 0)
      .join(" | "),
    PAGE_MARGIN,
    y + 14,
    CONTENT_WIDTH,
    11,
  );

  y += 18;
  pdf.setTextColor(138, 105, 54);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(22);
  pdf.text("QUOTATION", PAGE_MARGIN, y);

  pdf.setTextColor(23, 25, 29);
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "normal");
  const metadata = [
    `Quotation no.: ${quotation.quotationNumber}`,
    `Issue date: ${quotation.issueDate}`,
    `Valid until: ${quotation.validUntil}`,
  ];
  metadata.forEach((value, index) => {
    pdf.text(value, 360, y - 26 + index * 13);
  });

  y += 24;
  pdf.setDrawColor(215, 217, 222);
  pdf.line(PAGE_MARGIN, y, PAGE_MARGIN + CONTENT_WIDTH, y);
  y += 18;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.text("PREPARED FOR", PAGE_MARGIN, y);
  pdf.setFontSize(12);
  pdf.text(quotation.customer.name, PAGE_MARGIN, y + 17);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.5);
  pdf.setTextColor(93, 98, 107);
  y = addTextBlock(
    pdf,
    [quotation.customer.address, quotation.customer.email, quotation.customer.phone]
      .filter((value) => value.trim().length > 0)
      .join(" | "),
    PAGE_MARGIN,
    y + 31,
    CONTENT_WIDTH,
    11,
  );

  y += 18;
  pdf.setTextColor(23, 25, 29);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.text(quotation.title, PAGE_MARGIN, y);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  y = addTextBlock(pdf, quotation.introduction, PAGE_MARGIN, y + 16, CONTENT_WIDTH, 13);

  y = addItemsTable(pdf, snapshot, "recurring", y + 12);
  y = addItemsTable(pdf, snapshot, "one-time", y);
  y = ensureSpace(pdf, y, 92);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  if (snapshot.totals.recurring.includedLineCount > 0) {
    const amount = snapshot.totals.recurring.authoritativeSubtotalMinor;
    pdf.text(
      `Recurring subtotal: ${
        amount === null
          ? "TBD"
          : formatMoney(amount, quotation.currency, quotation.currencyMinorUnits)
      }`,
      PAGE_MARGIN + CONTENT_WIDTH,
      y,
      { align: "right" },
    );
    y += 15;
  }
  if (snapshot.totals.oneTime.includedLineCount > 0) {
    const amount = snapshot.totals.oneTime.authoritativeSubtotalMinor;
    pdf.text(
      `One-time subtotal: ${
        amount === null
          ? "TBD"
          : formatMoney(amount, quotation.currency, quotation.currencyMinorUnits)
      }`,
      PAGE_MARGIN + CONTENT_WIDTH,
      y,
      { align: "right" },
    );
    y += 15;
  }

  pdf.setFontSize(13);
  pdf.text(
    `Grand total: ${
      snapshot.totals.grandTotalMinor === null
        ? "TBD"
        : formatMoney(
            snapshot.totals.grandTotalMinor,
            quotation.currency,
            quotation.currencyMinorUnits,
          )
    }`,
    PAGE_MARGIN + CONTENT_WIDTH,
    y + 4,
    { align: "right" },
  );
  y += 34;

  if (quotation.terms.some((term) => term.trim().length > 0)) {
    y = ensureSpace(pdf, y, 72);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.text("Terms", PAGE_MARGIN, y);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    for (const term of quotation.terms) {
      if (term.trim().length === 0) {
        continue;
      }
      y = ensureSpace(pdf, y + 14, 34);
      y = addTextBlock(pdf, `- ${term.trim()}`, PAGE_MARGIN, y, CONTENT_WIDTH, 13);
    }
  }

  if (quotation.preparedBy.trim().length > 0) {
    y = ensureSpace(pdf, y + 22, 36);
    pdf.setFont("helvetica", "bold");
    pdf.text("Prepared by", PAGE_MARGIN, y);
    pdf.setFont("helvetica", "normal");
    pdf.text(quotation.preparedBy, PAGE_MARGIN, y + 14);
  }

  if (quotation.closingNote.trim().length > 0) {
    y = ensureSpace(pdf, y + 32, 40);
    pdf.setFont("helvetica", "italic");
    addTextBlock(pdf, quotation.closingNote, PAGE_MARGIN, y, CONTENT_WIDTH, 13);
  }

  return pdf;
}

export function generateQuotationPdf(
  quotation: Quotation,
  now = new Date(),
): Promise<GeneratedQuotationFile> {
  return Promise.resolve().then(() => {
    const snapshot = createQuotationExportSnapshot(quotation, now);
    const pdf = createPdfDocument(snapshot);
    const blob = new Blob([pdf.output("arraybuffer")], {
      type: "application/pdf",
    });

    return {
      name: createQuotationFileName(snapshot, "pdf"),
      type: "application/pdf",
      blob,
      snapshot,
    };
  });
}
